import { useEffect, useState } from 'react';
import { QueryClient } from 'react-query';
import { persistQueryClient } from 'react-query/persistQueryClient-experimental';

import { AppSettingsDataClient, QUERY_KEYS } from '@24i/nxg-sdk-photon';
import { logWarn, logError, log } from '@24i/nxg-core-utils/src/logger';

import { DEFAULT_RQ_CONFIG } from '../client';
import { getServiceConfig } from '../../../utils/configuration';
import QueryPersistCacheInstance from '../queryPersistCacheInstance';
import type { RQRemote, FullCfg, TierCfg, TierName, KeyOverride, KeyOverrideEntry } from '../types';

/**
 * Global debug interface for React Query configuration inspection
 */
declare global {
    interface Window {
        __REACT_QUERY_DEBUG__?: {
            getFullConfig: () => {
                tiers: Record<TierName, TierCfg>;
                keyGroups: Record<TierName, Array<string | string[]>>;
                keyOverrides?: KeyOverrideEntry[];
                version: string;
                persist: FullCfg['persist'];
                refetch: FullCfg['refetch'];
            };
            getKeyConfig: (key: string | string[]) => {
                tier: TierName | null;
                tierSettings: TierCfg | null;
                override: KeyOverride | null;
                finalConfig: unknown | null;
                keyString: string;
            };
            listAllKeys: () => Array<{ key: string; tier: TierName }>;
        };
    }
}

/**
 * Helper type for query keys that can be strings or arrays
 */
type KeyLike = string | string[];

/**
 * Serializes a key to a JSON string for comparison and object key usage
 */
function serializeKey(key: KeyLike): string {
    return Array.isArray(key) ? JSON.stringify(key) : key;
}

/**
 * Checks if two keys are equal (deep equality for arrays)
 */
function isEqualKey(a: KeyLike, b: KeyLike): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((val, idx) => val === b[idx]);
    }
    return a === b;
}

/**
 * Validates the version field
 */
function validateVersion(version?: string): string {
    const defaultVersion = '1.0.0';
    if (!version) return defaultVersion;

    // Basic semver validation
    const semverPattern = /^\d+\.\d+\.\d+$/;
    if (!semverPattern.test(version)) {
        logWarn('Invalid version format in remote config, using default:', {
            provided: version,
            default: defaultVersion,
        });
        return defaultVersion;
    }

    return version;
}

/**
 * Validates and coerces a numeric value, allowing string numbers
 */
function validateNumber(value: unknown, fieldName: string, keyStr: string): number | undefined {
    if (value === undefined || value === null) return undefined;

    // Allow numbers
    if (typeof value === 'number') {
        if (Number.isNaN(value) || value < 0) {
            logError(`Invalid ${fieldName} for ${keyStr} (must be non-negative number):`, {
                key: keyStr,
                value,
            });
            return undefined;
        }
        return value;
    }

    // Allow string numbers and try to parse
    if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed) && parsed >= 0) {
            logWarn(`Coercing string to number for ${fieldName}:`, {
                key: keyStr,
                original: value,
                coerced: parsed,
            });
            return parsed;
        }
    }

    // Invalid type
    logError(`Invalid ${fieldName} for ${keyStr} (expected number, got ${typeof value}):`, {
        key: keyStr,
        value,
    });
    return undefined;
}

/**
 * Validates a boolean value
 */
function validateBoolean(value: unknown, fieldName: string, keyStr: string): boolean | undefined {
    if (value === undefined || value === null) return undefined;

    if (typeof value === 'boolean') {
        return value;
    }

    logError(`Invalid ${fieldName} for ${keyStr} (expected boolean, got ${typeof value}):`, {
        key: keyStr,
        value,
    });
    return undefined;
}

/**
 * Validates refetchOnMount value (boolean or 'always')
 */
function validateRefetchOnMount(value: unknown, keyStr: string): boolean | 'always' | undefined {
    if (value === undefined || value === null) return undefined;

    if (typeof value === 'boolean') {
        return value;
    }

    if (value === 'always') {
        return 'always';
    }

    logError(
        `Invalid refetchOnMount for ${keyStr} (expected boolean or 'always', got ${typeof value}):`,
        {
            key: keyStr,
            value,
        }
    );
    return undefined;
}

/**
 * Validates and sanitizes a tier configuration
 * @param tierName - Name of the tier being validated
 * @param tierCfg - Tier configuration to validate
 * @param defaultCfg - Default tier configuration for fallback
 * @returns Validated tier configuration
 */
function validateTierConfig(
    tierName: TierName,
    tierCfg: Partial<TierCfg>,
    defaultCfg: TierCfg
): TierCfg {
    const validated: TierCfg = { ...defaultCfg };

    // Validate staleTimeMs
    const staleTime = validateNumber(tierCfg.staleTimeMs, 'staleTimeMs', `tier:${tierName}`);
    if (staleTime !== undefined) {
        validated.staleTimeMs = staleTime;
    }

    // Validate cacheTimeMs
    const cacheTime = validateNumber(tierCfg.cacheTimeMs, 'cacheTimeMs', `tier:${tierName}`);
    if (cacheTime !== undefined) {
        validated.cacheTimeMs = cacheTime;
    }

    // Validate refetchOnMount
    const refetchOnMount = validateRefetchOnMount(tierCfg.refetchOnMount, `tier:${tierName}`);
    if (refetchOnMount !== undefined) {
        validated.refetchOnMount = refetchOnMount;
    }

    // Validate refetchOnWindowFocus
    const refetchOnWindowFocus = validateBoolean(
        tierCfg.refetchOnWindowFocus,
        'refetchOnWindowFocus',
        `tier:${tierName}`
    );
    if (refetchOnWindowFocus !== undefined) {
        validated.refetchOnWindowFocus = refetchOnWindowFocus;
    }

    // Validate refetchOnReconnect
    const refetchOnReconnect = validateBoolean(
        tierCfg.refetchOnReconnect,
        'refetchOnReconnect',
        `tier:${tierName}`
    );
    if (refetchOnReconnect !== undefined) {
        validated.refetchOnReconnect = refetchOnReconnect;
    }

    return validated;
}

/**
 * Merges default tier configurations with remote tier overrides
 * @param defaultTiers - Default tier configurations
 * @param remoteTiers - Optional remote tier configuration overrides
 * @returns Merged tier configurations
 */
function mergeTiers(
    defaultTiers: Record<TierName, TierCfg>,
    remoteTiers?: Partial<Record<TierName, Partial<TierCfg>>>
): Record<TierName, TierCfg> {
    if (!remoteTiers) return defaultTiers;
    const merged = { ...defaultTiers };
    const tierEntries = Object.entries(remoteTiers) as Array<[TierName, Partial<TierCfg>]>;
    tierEntries.forEach(([tier, tierCfg]) => {
        merged[tier] = validateTierConfig(tier, tierCfg, defaultTiers[tier]);
    });
    return merged;
}

/**
 * Merges key groups with atomic key moves (removes key from all groups before adding to target)
 * @param defaultGroups - Default key group configuration
 * @param remoteGroups - Optional remote key group overrides
 * @returns Merged key groups with atomic moves applied
 */
function mergeKeyGroups(
    defaultGroups: Record<TierName, Array<string | string[]>>,
    remoteGroups?: Partial<Record<TierName, Array<string | string[]>>>
): Record<TierName, Array<string | string[]>> {
    if (!remoteGroups) return defaultGroups;

    // Start with copy of defaults
    const merged: Record<TierName, Array<string | string[]>> = {
        absoluteZero: [...defaultGroups.absoluteZero],
        zero: [...defaultGroups.zero],
        short: [...defaultGroups.short],
        medium: [...defaultGroups.medium],
        long: [...defaultGroups.long],
    };

    // Track which keys have been moved to avoid duplicates
    const movedKeys = new Set<string>();
    let invalidConfigFound = false;

    // Process each remote group
    const remoteEntries = Object.entries(remoteGroups) as Array<
        [TierName, Array<string | string[]>]
    >;
    remoteEntries.forEach(([targetTier, keys]) => {
        // Validate that keys is an array
        if (!Array.isArray(keys)) {
            logError(
                `[ReactQuery] Invalid remote config: keyGroups.${targetTier} must be an array, got ${typeof keys}`,
                {
                    tier: targetTier,
                    receivedType: typeof keys,
                    receivedValue: keys,
                    expectedFormat: 'Array of strings or string arrays',
                    example: `"keyGroups": { "${targetTier}": ["key1", "key2"] }`,
                }
            );
            invalidConfigFound = true;
            return; // Skip this tier
        }
        keys.forEach((key) => {
            const keyStr = serializeKey(key);

            // Check if key appears in multiple groups in remote config
            if (movedKeys.has(keyStr)) {
                logWarn('Key appears in multiple groups in remote config, last one wins:', {
                    key: keyStr,
                    targetTier,
                });
            }
            movedKeys.add(keyStr);

            // Remove key from ALL groups (atomic move)
            (['absoluteZero', 'zero', 'short', 'medium', 'long'] as TierName[]).forEach((tier) => {
                merged[tier] = merged[tier].filter((existingKey) => !isEqualKey(existingKey, key));
            });

            // Add key to target group
            merged[targetTier].push(key);
        });
    });

    if (invalidConfigFound) {
        logError(
            '[ReactQuery] Remote configuration contains invalid keyGroups format. ' +
                'Configuration was partially applied. Please fix the remote config in Backstage.',
            {
                validFormat: {
                    keyGroups: {
                        absoluteZero: ['key1', 'key2'],
                        zero: [['composed', 'key'], 'simpleKey'],
                    },
                },
            }
        );
    }

    return merged;
}

/**
 * Validates a single key format
 */
function validateKeyFormat(key: KeyLike): boolean {
    // Runtime validation: key must be string or string[] only
    if (typeof key !== 'string' && !Array.isArray(key)) {
        logError('[ReactQuery] Invalid key format in keyOverrides', {
            expected: 'string or string[]',
            received: typeof key,
            value: key,
        });
        return false;
    }

    // If array, validate all elements are strings
    if (Array.isArray(key)) {
        const allStrings = key.every((k) => typeof k === 'string');
        if (!allStrings) {
            logError('[ReactQuery] Invalid key format in keyOverrides', {
                message: 'array elements must all be strings',
                value: key,
            });
            return false;
        }
    }

    return true;
}

/**
 * Validates and builds override options from raw override object
 */
function validateOverrideOptions(override: KeyOverride, keyStr: string): KeyOverride | null {
    const validated: KeyOverride = {};
    let hasValidFields = false;

    // Validate numeric fields
    const staleTime = validateNumber(override.staleTime, 'staleTime', `key:${keyStr}`);
    if (staleTime !== undefined) {
        validated.staleTime = staleTime;
        hasValidFields = true;
    }

    const cacheTime = validateNumber(override.cacheTime, 'cacheTime', `key:${keyStr}`);
    if (cacheTime !== undefined) {
        validated.cacheTime = cacheTime;
        hasValidFields = true;
    }

    const refetchInterval = validateNumber(
        override.refetchInterval,
        'refetchInterval',
        `key:${keyStr}`
    );
    if (refetchInterval !== undefined) {
        validated.refetchInterval = refetchInterval;
        hasValidFields = true;
    }

    // Validate refetchOnMount
    const refetchOnMount = validateRefetchOnMount(override.refetchOnMount, `key:${keyStr}`);
    if (refetchOnMount !== undefined) {
        validated.refetchOnMount = refetchOnMount;
        hasValidFields = true;
    }

    // Validate boolean fields
    const refetchOnWindowFocus = validateBoolean(
        override.refetchOnWindowFocus,
        'refetchOnWindowFocus',
        `key:${keyStr}`
    );
    if (refetchOnWindowFocus !== undefined) {
        validated.refetchOnWindowFocus = refetchOnWindowFocus;
        hasValidFields = true;
    }

    const refetchOnReconnect = validateBoolean(
        override.refetchOnReconnect,
        'refetchOnReconnect',
        `key:${keyStr}`
    );
    if (refetchOnReconnect !== undefined) {
        validated.refetchOnReconnect = refetchOnReconnect;
        hasValidFields = true;
    }

    if (!hasValidFields) {
        logWarn('Skipping key override with no valid fields:', {
            key: keyStr,
            providedFields: Object.keys(override),
        });
        return null;
    }

    return validated;
}

/**
 * Applies a single key override to QueryClient
 */
function applySingleKeyOverride(
    queryClient: QueryClient,
    key: KeyLike,
    validated: KeyOverride,
    knownKeys?: Set<string>
): void {
    const keyStr = serializeKey(key);
    const queryKey = Array.isArray(key) ? key : [key];

    // Check if key exists in any tier group
    if (knownKeys && !knownKeys.has(keyStr)) {
        logWarn(
            '[ReactQuery] Applying override for key not found in any tier group (may be dynamic):',
            {
                key: keyStr,
                options: validated,
            }
        );
    }

    // Merge with existing tier defaults instead of replacing
    const existingDefaults = queryClient.getQueryDefaults(queryKey) || {};
    const merged = { ...existingDefaults, ...validated };
    queryClient.setQueryDefaults(queryKey, merged);
}

/**
 * Validates and applies per-key overrides to QueryClient
 * @param queryClient - QueryClient instance
 * @param keyOverrides - Array of per-key configuration overrides
 * @param keyGroups - Current key groups for validation warning
 */
function applyKeyOverrides(
    queryClient: QueryClient,
    keyOverrides?: KeyOverrideEntry[],
    keyGroups?: Record<TierName, Array<string | string[]>>
): void {
    if (!keyOverrides || !Array.isArray(keyOverrides)) return;

    // Build a set of all known keys for validation
    const knownKeys = new Set<string>();
    if (keyGroups) {
        Object.values(keyGroups).forEach((keys) => {
            keys.forEach((key) => {
                knownKeys.add(serializeKey(key));
            });
        });
    }

    keyOverrides.forEach(({ key, override }) => {
        // Validate key format
        if (!validateKeyFormat(key)) return;

        // Validate and build override options
        const keyStr = serializeKey(key);
        const validated = validateOverrideOptions(override, keyStr);
        if (!validated) return;

        // Apply the override
        applySingleKeyOverride(queryClient, key, validated, knownKeys);
    });
}

/**
 * Merges default React Query configuration with remote overrides
 * @param defaults - Default full configuration
 * @param remote - Optional remote configuration overrides
 * @returns Merged full configuration
 */
function mergeRQConfig(defaults: FullCfg, remote?: RQRemote): FullCfg {
    if (!remote) return defaults;

    const version = validateVersion(remote.version);

    const merged: FullCfg = {
        ...defaults,
        version,
        refetch: { ...defaults.refetch, ...remote.refetch },
        tiers: mergeTiers(defaults.tiers, remote.tiers),
        keyGroups: mergeKeyGroups(defaults.keyGroups, remote.keyGroups),
        keyOverrides: remote.keyOverrides ?? undefined,
        persist: { ...defaults.persist, ...remote.persist },
        staleTimeMs: remote.staleTimeMs ?? defaults.staleTimeMs,
        cacheTimeMs: remote.cacheTimeMs ?? defaults.cacheTimeMs,
        enabled: remote.enabled ?? defaults.enabled,
        allStale: remote.allStale ?? defaults.allStale,
    };

    return merged;
}

/**
 * Converts a query key to an array of strings
 */
const asArrayKey = (key: KeyLike): string[] =>
    Array.isArray(key) ? key.map(String) : [String(key)];

/**
 * Gets the base (first element) of a query key
 */
const baseOf = (key: KeyLike): string => asArrayKey(key)[0];

/**
 * Builds a list of allowed prefixes for persistence based on configuration
 * Excludes absoluteZero tier and applies blacklist/whitelist rules
 */
function buildAllowedPrefixes(configuration: FullCfg): string[][] {
    // 1) Collect all prefixes as arrays of strings
    const rawPrefixes: string[][] = (Object.values(configuration.keyGroups) as KeyLike[][])
        .flat()
        .map(asArrayKey);

    // 2) Dedupe preserving order
    const uniquePrefixes = new Map<string, string[]>();
    rawPrefixes.forEach((prefix) => {
        const key = JSON.stringify(prefix);
        if (!uniquePrefixes.has(key)) uniquePrefixes.set(key, prefix);
    });
    let prefixes = Array.from(uniquePrefixes.values());

    // 3) Exclude absoluteZero
    const absoluteZeroBases = new Set(configuration.keyGroups.absoluteZero.map(baseOf));
    prefixes = prefixes.filter((prefix) => !absoluteZeroBases.has(baseOf(prefix)));

    // 4) Apply persist policy
    if (configuration.persist.mode === 'blacklist') {
        const deny = new Set(configuration.persist.blacklistKeys.map(String));
        prefixes = prefixes.filter((prefix) => !deny.has(baseOf(prefix)));
        return prefixes;
    }

    // whitelist mode
    const allow = new Set(configuration.persist.whitelistKeys.map(String));
    prefixes = prefixes.filter((prefix) => allow.has(baseOf(prefix)));
    return prefixes;
}

/**
 * Creates pre-computed sets for efficient query key filtering during dehydration
 */
function createDehydrationFilters(config: FullCfg) {
    const absoluteZeroKeys = new Set<string>();
    const absoluteZeroPrefixes = new Set<string>();
    config.keyGroups.absoluteZero.forEach((key) => {
        const keyStr = Array.isArray(key) ? key.join('/') : String(key);
        absoluteZeroKeys.add(keyStr);
        absoluteZeroPrefixes.add(`${keyStr}/`);
    });

    const filterKeys = new Set<string>();
    const filterPrefixes = new Set<string>();
    const filterList =
        config.persist.mode === 'blacklist'
            ? config.persist.blacklistKeys
            : config.persist.whitelistKeys;
    filterList.forEach((key) => {
        const keyStr = String(key);
        filterKeys.add(keyStr);
        filterPrefixes.add(`${keyStr}/`);
    });

    return { absoluteZeroKeys, absoluteZeroPrefixes, filterKeys, filterPrefixes };
}

/**
 * Determines if a query should be persisted based on configuration and filters
 */
function shouldDehydrateQuery(
    query: { state: { status: string }; queryKey: unknown },
    config: FullCfg,
    filters: ReturnType<typeof createDehydrationFilters>
): boolean {
    if (query.state.status !== 'success') return false;

    const queryKeyStr = Array.isArray(query.queryKey)
        ? query.queryKey.join('/')
        : String(query.queryKey);

    const { absoluteZeroKeys, absoluteZeroPrefixes, filterKeys, filterPrefixes } = filters;

    // absoluteZero never persists - O(1) lookup
    if (absoluteZeroKeys.has(queryKeyStr)) return false;

    // Check prefix match
    const absoluteZeroPrefixArray = Array.from(absoluteZeroPrefixes);
    if (absoluteZeroPrefixArray.some((prefix) => queryKeyStr.startsWith(prefix))) {
        return false;
    }

    // Apply blacklist/whitelist - O(1) lookup
    const isBlacklist = config.persist.mode === 'blacklist';
    const isMatch =
        filterKeys.has(queryKeyStr) ||
        Array.from(filterPrefixes).some((prefix) => queryKeyStr.startsWith(prefix));

    // blacklist: persist if NOT matched, whitelist: persist if matched
    return isBlacklist ? !isMatch : isMatch;
}

/**
 * Applies tier defaults to all keys in each tier using setQueryDefaults.
 * Clears all existing defaults first, then rebuilds from merged config (defaults + remote).
 * This ensures remote config is the source of truth with no stale defaults.
 */
function applyTierDefaults(
    queryClient: QueryClient,
    keyGroups: Record<TierName, KeyLike[]>,
    tiers: Record<TierName, TierCfg>
): void {
    const tierNames: TierName[] = ['absoluteZero', 'zero', 'short', 'medium', 'long'];

    // Clear all existing query defaults (React Query v3 internal API)
    const { queryDefaults } = queryClient as unknown as { queryDefaults?: unknown[] };

    if (queryDefaults && Array.isArray(queryDefaults)) {
        // Clear the entire array to remove all defaults (initial + any previous remote config)
        queryDefaults.length = 0;
    }

    // Collect all keys with their tier and length
    const allKeysWithTier: Array<{ key: KeyLike; tier: TierName; length: number }> = [];

    tierNames.forEach((tierName) => {
        const keys = keyGroups[tierName] || [];
        keys.forEach((key) => {
            const length = Array.isArray(key) ? key.length : 1;
            allKeysWithTier.push({ key, tier: tierName, length });
        });
    });

    // Sort by length (LONGER prefixes first) - React Query v3 matches first entry in defaults array
    // More specific keys must be registered BEFORE their parent prefixes to win the match
    allKeysWithTier.sort((a, b) => {
        // Primary: sort by array length (LONGER first - reverse order)
        if (a.length !== b.length) {
            return b.length - a.length; // Reversed: longer keys first
        }

        // Secondary: For same length, ensure consistent ordering by comparing key strings
        const aStr = JSON.stringify(a.key);
        const bStr = JSON.stringify(b.key);
        return aStr.localeCompare(bStr);
    });

    // Apply defaults in order of decreasing specificity (most specific first)
    allKeysWithTier.forEach(({ key, tier }) => {
        const tierCfg = tiers[tier];
        if (!tierCfg) return;

        // Build the query defaults from tier config
        const defaults: Record<string, unknown> = {
            staleTime: tierCfg.staleTimeMs,
            cacheTime: tierCfg.cacheTimeMs,
        };

        // Add optional fields if they exist
        if (tierCfg.refetchOnMount !== undefined) {
            defaults.refetchOnMount = tierCfg.refetchOnMount;
        }
        if (tierCfg.refetchOnWindowFocus !== undefined) {
            defaults.refetchOnWindowFocus = tierCfg.refetchOnWindowFocus;
        }
        if (tierCfg.refetchOnReconnect !== undefined) {
            defaults.refetchOnReconnect = tierCfg.refetchOnReconnect;
        }

        // Ensure key is always an array for React Query v3 compatibility
        const queryKey = Array.isArray(key) ? key : [key];

        // Set the defaults (array is already cleared, so this is a fresh add)
        queryClient.setQueryDefaults(queryKey, defaults);
    });
}

function applyConfigToQueryClient(queryClient: QueryClient, config: FullCfg): void {
    // Apply global defaults
    queryClient.setDefaultOptions({
        queries: {
            staleTime: config.staleTimeMs,
            cacheTime: config.cacheTimeMs,
            refetchOnWindowFocus: config.refetch.onFocus,
            refetchOnReconnect: config.refetch.onReconnect,
            refetchOnMount: config.refetch.onMount,
        },
    });

    // Apply tier defaults for each key in each tier (medium priority)
    applyTierDefaults(queryClient, config.keyGroups, config.tiers);

    // Apply per-key overrides (highest priority)
    applyKeyOverrides(queryClient, config.keyOverrides, config.keyGroups);
}

/**
 * Sets up query persistence based on configuration
 */
async function setupQueryPersistence(queryClient: QueryClient, config: FullCfg): Promise<void> {
    const allowedPrefixes = buildAllowedPrefixes(config);
    QueryPersistCacheInstance.init(allowedPrefixes, config.persist.throttleTimeMs);

    const persistor = QueryPersistCacheInstance.getInstance()?.getPersistor();
    if (!persistor) return;

    const filters = createDehydrationFilters(config);

    try {
        await persistQueryClient({
            queryClient,
            persistor,
            maxAge: config.persist.maxAgeMs,
            buster: config.persist.buster,
            dehydrateOptions: {
                shouldDehydrateQuery: (query) => shouldDehydrateQuery(query, config, filters),
            },
        });
    } catch (error) {
        logWarn('Failed to setup query persistence:', {
            error,
            persistConfig: config.persist,
            message: error instanceof Error ? error.message : String(error),
        });
        // Continue without persistence
    }
}

/**
 * Applies remote configuration to QueryClient
 */
async function applyRemoteConfiguration(
    queryClient: QueryClient,
    remoteConfig: unknown
): Promise<FullCfg> {
    // Map AppSettings to RQRemote shape
    const rqOverride: RQRemote =
        (remoteConfig as { features?: { reactQuerySettings?: RQRemote } })?.features
            ?.reactQuerySettings ?? {};

    // Merge remote config override with default
    const config = mergeRQConfig(DEFAULT_RQ_CONFIG, rqOverride);

    // Apply config to QueryClient
    applyConfigToQueryClient(queryClient, config);

    // Setup persist config if enabled (both root enabled and persist.enabled must be true)
    if (config.enabled && config.persist.enabled) {
        await setupQueryPersistence(queryClient, config);
    }

    return config;
}

/**
 * Exposes debug methods on window for configuration inspection
 * Works on all platforms (web, iOS, Android)
 */
function setupDebugMethods(config: FullCfg): () => void {
    const findKeyTier = (key: KeyLike): TierName | null => {
        const keyStr = serializeKey(key);

        const tierEntries = Object.entries(config.keyGroups) as Array<
            [TierName, Array<string | string[]>]
        >;
        const foundEntry = tierEntries.find(([, keys]) =>
            keys.some((k) => serializeKey(k) === keyStr)
        );

        return foundEntry ? foundEntry[0] : null;
    };

    const getKeyOverride = (key: KeyLike): KeyOverride | null => {
        if (!config.keyOverrides) return null;

        const keyStr = serializeKey(key);
        const override = config.keyOverrides.find((entry) => serializeKey(entry.key) === keyStr);
        return override?.override || null;
    };

    const getFinalConfig = (
        tier: TierName | null,
        override: KeyOverride | null
    ): unknown | null => {
        if (!tier && !override) return null;

        const tierSettings = tier ? config.tiers[tier] : {};
        return {
            ...tierSettings,
            ...override,
        };
    };

    // Expose debug methods on window (global for React Native debugger) - only in dev mode
    if (__DEV__) {
        // eslint-disable-next-line no-underscore-dangle
        (global as unknown as Window).__REACT_QUERY_DEBUG__ = {
            /**
             * Get the full merged configuration
             * @example
             * // In browser console or React Native debugger:
             * __REACT_QUERY_DEBUG__.getFullConfig()
             */
            getFullConfig: () => ({
                tiers: config.tiers,
                keyGroups: config.keyGroups,
                keyOverrides: config.keyOverrides,
                version: config.version,
                persist: config.persist,
                refetch: config.refetch,
            }),

            /**
             * Get configuration for a specific query key
             * @example
             * // For string key:
             * __REACT_QUERY_DEBUG__.getKeyConfig('user')
             *
             * // For composed key:
             * __REACT_QUERY_DEBUG__.getKeyConfig(['playlists', 'user-continue-watching'])
             */
            getKeyConfig: (key: string | string[]) => {
                const tier = findKeyTier(key);
                const tierSettings = tier ? config.tiers[tier] : null;
                const override = getKeyOverride(key);
                const finalConfig = getFinalConfig(tier, override);

                return {
                    tier,
                    tierSettings,
                    override,
                    finalConfig,
                    keyString: serializeKey(key),
                };
            },

            /**
             * List all configured keys with their assigned tiers
             * @example __REACT_QUERY_DEBUG__.listAllKeys()
             */
            listAllKeys: () => {
                const allKeys: Array<{ key: string; tier: TierName }> = [];

                const tierEntries = Object.entries(config.keyGroups) as Array<
                    [TierName, Array<string | string[]>]
                >;
                tierEntries.forEach(([tier, keys]) => {
                    keys.forEach((key) => {
                        allKeys.push({
                            key: serializeKey(key),
                            tier,
                        });
                    });
                });

                return allKeys.sort((a, b) => a.key.localeCompare(b.key));
            },
        };

        log(
            '[ReactQuery] 🐛 Debug methods exposed on global.__REACT_QUERY_DEBUG__\n' +
                'Available methods:\n' +
                '  - getFullConfig(): Get complete merged configuration\n' +
                '  - getKeyConfig(key): Get config for specific key\n' +
                '  - listAllKeys(): List all keys with tier assignments\n' +
                'Examples:\n' +
                '  __REACT_QUERY_DEBUG__.getFullConfig()\n' +
                '  __REACT_QUERY_DEBUG__.getKeyConfig("user")\n' +
                '  __REACT_QUERY_DEBUG__.getKeyConfig(["playlists", "user-continue-watching"])\n' +
                '  __REACT_QUERY_DEBUG__.listAllKeys()'
        );
    }

    // Return cleanup function
    return () => {
        if (__DEV__) {
            // eslint-disable-next-line no-underscore-dangle
            delete (global as unknown as Window).__REACT_QUERY_DEBUG__;
            log('[ReactQuery] 🐛 Debug methods removed from global');
        }
    };
}

// Setup QueryClient with backend override and persist config
export const useRemoteConfiguration = (
    queryClient: QueryClient,
    appSettingsDataClient: AppSettingsDataClient
) => {
    const [isFinished, setIsFinished] = useState(false);

    useEffect(() => {
        if (!appSettingsDataClient) {
            setIsFinished(true);
            return undefined;
        }

        setIsFinished(false);

        let cleanupDebug: (() => void) | null = null;

        queryClient
            .fetchQuery({
                queryKey: QUERY_KEYS.config,
                queryFn: () => getServiceConfig(appSettingsDataClient),
            })
            .then((remoteConfig) => applyRemoteConfiguration(queryClient, remoteConfig))
            .then((config) => {
                // Setup debug methods with merged config
                cleanupDebug = setupDebugMethods(config);
            })
            .catch((error) => {
                logWarn('Failed to apply remote configuration:', error);
                // Continue with default configuration - app should still work
                // Setup debug methods with default config
                cleanupDebug = setupDebugMethods(DEFAULT_RQ_CONFIG);
            })
            .finally(() => {
                setIsFinished(true);
            });

        // Cleanup debug methods on unmount
        return () => {
            if (cleanupDebug) {
                cleanupDebug();
            }
        };
    }, [appSettingsDataClient, queryClient]);

    return { isFinished };
};
